import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { invitationsAPI } from '../services/api'
import {
  ArrowLeft, Calendar, Euro, Building, Clock,
  CheckCircle, XCircle, AlertCircle, Mail,
  Briefcase, MessageSquare
} from 'lucide-react'

const MyInvitationsPage = () => {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [actionLoading, setActionLoading] = useState(null)
  const [declineId, setDeclineId] = useState(null)
  const [declineReason, setDeclineReason] = useState('')

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'creator') {
      navigate('/dashboard')
      return
    }
    fetchInvitations()
  }, [isAuthenticated, user])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const response = await invitationsAPI.getMy()
      setInvitations(response.data.invitations || [])
    } catch (err) {
      console.error('Error fetching invitations:', err)
      setError('Er is een fout opgetreden bij het laden van je uitnodigingen')
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (id) => {
    try {
      setActionLoading(id)
      await invitationsAPI.accept(id)
      await fetchInvitations()
    } catch (err) {
      const msg = err.response?.data?.error || 'Er is een fout opgetreden'
      setError(msg)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDecline = async (id) => {
    try {
      setActionLoading(id)
      await invitationsAPI.decline(id, { reason: declineReason })
      setDeclineId(null)
      setDeclineReason('')
      await fetchInvitations()
    } catch (err) {
      const msg = err.response?.data?.error || 'Er is een fout opgetreden'
      setError(msg)
    } finally {
      setActionLoading(null)
    }
  }

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'In afwachting',
        icon: Clock,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200'
      },
      accepted: {
        label: 'Geaccepteerd',
        icon: CheckCircle,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      },
      declined: {
        label: 'Afgewezen',
        icon: XCircle,
        color: 'text-red-500',
        bg: 'bg-red-50',
        border: 'border-red-200'
      },
      cancelled: {
        label: 'Geannuleerd',
        icon: AlertCircle,
        color: 'text-gray-500',
        bg: 'bg-gray-100',
        border: 'border-gray-200'
      }
    }
    return configs[status] || configs.pending
  }

  const filteredInvitations = invitations.filter(inv => {
    if (filter === 'all') return true
    if (filter === 'pending') return inv.status === 'pending'
    if (filter === 'accepted') return inv.status === 'accepted'
    if (filter === 'declined') return inv.status === 'declined'
    return true
  })

  const stats = {
    total: invitations.length,
    pending: invitations.filter(i => i.status === 'pending').length,
    accepted: invitations.filter(i => i.status === 'accepted').length,
    declined: invitations.filter(i => i.status === 'declined').length
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1D8BF8] mx-auto mb-4"></div>
          <p className="text-[#475569]">Uitnodigingen laden...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2.5 -ml-2 hover:bg-[#F8FAFC] rounded-lg transition">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold">Mijn Uitnodigingen</h1>
                <p className="text-sm text-[#475569]">
                  Opdrachten waarvoor je bent uitgenodigd
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8">
          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Mail className="w-6 h-6 md:w-8 md:h-8 text-[#3EB489]" />
              <span className="text-2xl md:text-3xl font-bold">{stats.total}</span>
            </div>
            <p className="text-sm text-[#475569]">Totaal Uitnodigingen</p>
          </div>
          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6 md:w-8 md:h-8 text-yellow-600" />
              <span className="text-2xl md:text-3xl font-bold">{stats.pending}</span>
            </div>
            <p className="text-sm text-[#475569]">In Afwachting</p>
          </div>
          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold">{stats.accepted}</span>
            </div>
            <p className="text-sm text-[#475569]">Geaccepteerd</p>
          </div>
          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
              <span className="text-2xl md:text-3xl font-bold">{stats.declined}</span>
            </div>
            <p className="text-sm text-[#475569]">Afgewezen</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {[
            { key: 'all', label: `Alle (${invitations.length})` },
            { key: 'pending', label: `In afwachting (${stats.pending})` },
            { key: 'accepted', label: `Geaccepteerd (${stats.accepted})` },
            { key: 'declined', label: `Afgewezen (${stats.declined})` }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                filter === tab.key
                  ? 'bg-sky-500 text-white'
                  : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Invitations List */}
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Geen uitnodigingen</h3>
            <p className="text-[#475569] mb-6">
              {filter === 'all'
                ? 'Je hebt nog geen uitnodigingen ontvangen'
                : `Geen uitnodigingen met deze status`
              }
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EB489] rounded-lg font-semibold hover:bg-[#359E78] transition text-white"
            >
              <Briefcase className="w-5 h-5" />
              Zoek Opdrachten
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInvitations.map((invitation) => {
              const statusConfig = getStatusConfig(invitation.status)
              const StatusIcon = statusConfig.icon
              const project = invitation.project

              return (
                <div key={invitation._id} className="p-4 sm:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Project Title */}
                      <Link
                        to={project?._id ? `/projects/${project._id}` : '#'}
                        className="text-xl font-bold break-words hover:text-[#3EB489] transition"
                      >
                        {project?.title || 'Opdracht niet beschikbaar'}
                      </Link>

                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-4 mt-2 mb-3 text-sm text-[#475569]">
                        {invitation.company?.companyName && (
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            <span>{invitation.company.companyName}</span>
                          </div>
                        )}
                        {project?.budget && (
                          <div className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            <span>€{project.budget}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(invitation.createdAt).toLocaleDateString('nl-NL')}
                          </span>
                        </div>
                      </div>

                      {/* Message */}
                      {invitation.message && (
                        <div className="p-3 bg-[#F8FAFC] rounded-lg mb-3">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-[#475569] mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-[#475569] break-words min-w-0">{invitation.message}</p>
                          </div>
                        </div>
                      )}

                      {/* Actions for pending invitations */}
                      {invitation.status === 'pending' && (
                        <div className="flex flex-wrap items-center gap-3 mt-4">
                          <button
                            onClick={() => handleAccept(invitation._id)}
                            disabled={actionLoading === invitation._id}
                            className="inline-flex items-center gap-2 px-5 py-2.5 sm:py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {actionLoading === invitation._id ? 'Bezig...' : 'Accepteer'}
                          </button>
                          {declineId === invitation._id ? (
                            /* Op mobiel een eigen volle rij: input over de hele breedte, knoppen eronder */
                            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                              <input
                                type="text"
                                value={declineReason}
                                onChange={e => setDeclineReason(e.target.value)}
                                placeholder="Reden (optioneel)"
                                className="w-full sm:w-auto min-w-0 sm:flex-1 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                              />
                              <button
                                onClick={() => handleDecline(invitation._id)}
                                disabled={actionLoading === invitation._id}
                                className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition disabled:opacity-50"
                              >
                                Bevestig
                              </button>
                              <button
                                onClick={() => { setDeclineId(null); setDeclineReason('') }}
                                className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-2 text-[#475569] hover:text-[#0F172A] text-sm"
                              >
                                Annuleer
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeclineId(invitation._id)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 sm:py-2 bg-red-50 text-red-500 border border-red-200 rounded-lg font-medium hover:bg-red-100 transition"
                            >
                              <XCircle className="w-4 h-4" />
                              Afwijzen
                            </button>
                          )}
                        </div>
                      )}

                      {/* Link to proposal if accepted */}
                      {invitation.status === 'accepted' && invitation.resultingProposal && (
                        <div className="mt-3">
                          <Link
                            to="/my-proposals"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100 transition"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Bekijk je voorstel
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Status Badge — op mobiel boven de titel, vanaf sm rechts */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg ${statusConfig.bg} ${statusConfig.border} border shrink-0 self-start order-first sm:order-none`}>
                      <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                      <span className={`font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyInvitationsPage
